import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Printer, Stethoscope } from 'lucide-react';
import Navbar from './Navbar';
import { useLanguage } from '../contexts/LanguageContext';

// Wilayas 01-58, memes cles que /medecins/:wilaya/:specialty (DoctorsByWilaya.js)
const WILAYA_OPTIONS = [
  ['adrar', '01 - Adrar', '01 - أدرار'], ['chlef', '02 - Chlef', '02 - الشلف'], ['laghouat', '03 - Laghouat', '03 - الأغواط'],
  ['oum-el-bouaghi', '04 - Oum El Bouaghi', '04 - أم البواقي'], ['batna', '05 - Batna', '05 - باتنة'], ['bejaia', '06 - Béjaïa', '06 - بجاية'],
  ['biskra', '07 - Biskra', '07 - بسكرة'], ['bechar', '08 - Béchar', '08 - بشار'], ['blida', '09 - Blida', '09 - البليدة'],
  ['bouira', '10 - Bouira', '10 - البويرة'], ['tamanrasset', '11 - Tamanrasset', '11 - تمنراست'], ['tebessa', '12 - Tébessa', '12 - تبسة'],
  ['tlemcen', '13 - Tlemcen', '13 - تلمسان'], ['tiaret', '14 - Tiaret', '14 - تيارت'], ['tizi-ouzou', '15 - Tizi Ouzou', '15 - تيزي وزو'],
  ['alger', '16 - Alger', '16 - الجزائر'], ['djelfa', '17 - Djelfa', '17 - الجلفة'], ['jijel', '18 - Jijel', '18 - جيجل'],
  ['setif', '19 - Sétif', '19 - سطيف'], ['saida', '20 - Saïda', '20 - سعيدة'], ['skikda', '21 - Skikda', '21 - سكيكدة'],
  ['sidi-bel-abbes', '22 - Sidi Bel Abbès', '22 - سيدي بلعباس'], ['annaba', '23 - Annaba', '23 - عنابة'], ['guelma', '24 - Guelma', '24 - قالمة'],
  ['constantine', '25 - Constantine', '25 - قسنطينة'], ['medea', '26 - Médéa', '26 - المدية'], ['mostaganem', '27 - Mostaganem', '27 - مستغانم'],
  ['msila', "28 - M'Sila", '28 - المسيلة'], ['mascara', '29 - Mascara', '29 - معسكر'], ['ouargla', '30 - Ouargla', '30 - ورقلة'],
  ['oran', '31 - Oran', '31 - وهران'], ['el-bayadh', '32 - El Bayadh', '32 - البيض'], ['illizi', '33 - Illizi', '33 - إليزي'],
  ['bordj-bou-arreridj', '34 - Bordj Bou Arréridj', '34 - برج بوعريريج'], ['boumerdes', '35 - Boumerdès', '35 - بومرداس'],
  ['el-tarf', '36 - El Tarf', '36 - الطارف'], ['tindouf', '37 - Tindouf', '37 - تندوف'], ['tissemsilt', '38 - Tissemsilt', '38 - تيسمسيلت'],
  ['el-oued', '39 - El Oued', '39 - الوادي'], ['khenchela', '40 - Khenchela', '40 - خنشلة'], ['souk-ahras', '41 - Souk Ahras', '41 - سوق أهراس'],
  ['tipaza', '42 - Tipaza', '42 - تيبازة'], ['mila', '43 - Mila', '43 - ميلة'], ['ain-defla', '44 - Aïn Defla', '44 - عين الدفلى'],
  ['naama', '45 - Naâma', '45 - النعامة'], ['ain-temouchent', '46 - Aïn Témouchent', '46 - عين تموشنت'], ['ghardaia', '47 - Ghardaïa', '47 - غرداية'],
  ['relizane', '48 - Relizane', '48 - غليزان'], ['timimoun', '49 - Timimoun', '49 - تيميمون'],
  ['bordj-badji-mokhtar', '50 - Bordj Badji Mokhtar', '50 - برج باجي مختار'], ['ouled-djellal', '51 - Ouled Djellal', '51 - أولاد جلال'],
  ['beni-abbes', '52 - Béni Abbès', '52 - بني عباس'], ['in-salah', '53 - In Salah', '53 - عين صالح'], ['in-guezzam', '54 - In Guezzam', '54 - عين قزام'],
  ['touggourt', '55 - Touggourt', '55 - تقرت'], ['djanet', '56 - Djanet', '56 - جانت'], ['el-mghaier', "57 - El M'Ghaier", '57 - المغير'],
  ['el-meniaa', '58 - El Meniaa', '58 - المنيعة'],
];

// Libellés du médecin recherché par spécialité, dans les deux langues.
const SPECIALTY_LABELS = {
  generaliste: { fr: 'un généraliste', ar: 'طبيب عام' },
  gynecologue: { fr: 'un gynécologue', ar: 'طبيب أمراض النساء' },
  pediatre: { fr: 'un pédiatre', ar: 'طبيب أطفال' },
  cardiologue: { fr: 'un cardiologue', ar: 'طبيب قلب' },
  dermatologue: { fr: 'un dermatologue', ar: 'طبيب جلدية' },
  dentiste: { fr: 'un dentiste', ar: 'طبيب أسنان' },
  ophtalmologue: { fr: 'un ophtalmologue', ar: 'طبيب عيون' },
  orthopediste: { fr: 'un orthopédiste', ar: 'طبيب عظام' },
  nutritionniste: { fr: 'un nutritionniste', ar: 'أخصائي تغذية' },
  endocrinologue: { fr: 'un endocrinologue', ar: 'طبيب غدد صماء' },
  psychiatre: { fr: 'un psychiatre', ar: 'طبيب نفسي' },
  pharmacien: { fr: 'un pharmacien', ar: 'صيدلي' },
  nephrologue: { fr: 'un néphrologue', ar: 'طبيب كلى' },
  pneumologue: { fr: 'un pneumologue', ar: 'طبيب أمراض الرئة' },
};

// specialty: slug utilise par /medecins/:wilaya/:specialty (ex: "pediatre", "gynecologue")
// specialtyLabel: texte affiche en repli si specialty n'est pas dans SPECIALTY_LABELS (ex: "un pediatre")
const ToolPageLayout = ({ title, subtitle, children, specialty, specialtyLabel }) => {
  const [wilaya, setWilaya] = useState('alger');
  const { t, lang, isRTL } = useLanguage();
  const BackIcon = isRTL ? ChevronRight : ChevronLeft;
  const resolvedSpecialtyLabel = SPECIALTY_LABELS[specialty]?.[lang] || specialtyLabel || t('tool_default_specialty_label');

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="print:hidden">
        <Navbar />
      </div>

      <section className="bg-gradient-to-r from-[#0596DE] to-[#047CBA] py-12 print:bg-none print:text-[#0F172A] print:py-4">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-white print:text-[#0F172A]">
          <div className="flex items-center justify-between gap-4 mb-4 print:hidden">
            <Link to="/outils-sante" className="inline-flex items-center gap-1 text-white/80 hover:text-white text-sm">
              <BackIcon className="w-4 h-4" />
              {t('tool_back_to_all')}
            </Link>
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-xs font-semibold px-3 py-2 rounded-lg transition-all shrink-0"
            >
              <Printer className="w-4 h-4" />
              {t('tool_print')}
            </button>
          </div>
          <h1 className="text-2xl md:text-4xl font-bold mb-2">{title}</h1>
          {subtitle && <p className="text-white/80 print:text-[#64748B]">{subtitle}</p>}
        </div>
      </section>

      <section className="py-12 print:py-4">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 md:p-8 shadow-sm print:border-0 print:p-0 print:shadow-none">
            {children}
          </div>

          {specialty && (
            <div className="mt-8 bg-gradient-to-br from-[#0F172A] to-[#1E293B] text-white rounded-2xl p-5 border border-[#0596DE]/20 print:hidden">
              <div className="flex items-center gap-2 text-[#38BDF8] mb-2">
                <Stethoscope className="w-5 h-5" />
                <span className="font-bold text-xs uppercase tracking-wider">{t('nav_search')}</span>
              </div>
              <p className="text-sm text-slate-200 mb-3">
                {t('tool_need_consult_prefix')}{resolvedSpecialtyLabel}{t('tool_need_consult_suffix')}
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <select
                  value={wilaya}
                  onChange={(e) => setWilaya(e.target.value)}
                  className="flex-1 bg-slate-800 text-sm text-slate-200 border border-slate-700 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-[#0596DE]"
                >
                  {WILAYA_OPTIONS.map(([slug, labelFr, labelAr]) => (
                    <option key={slug} value={slug}>{isRTL ? labelAr : labelFr}</option>
                  ))}
                </select>
                <Link
                  to={`/medecins/${wilaya}/${specialty}`}
                  className="inline-flex items-center justify-center gap-2 bg-[#0596DE] hover:bg-[#047CBA] text-white font-semibold text-sm px-4 py-2 rounded-lg transition-all whitespace-nowrap"
                >
                  {t('tool_search_cta')}
                </Link>
              </div>
            </div>
          )}

          <p className="text-xs text-[#94A3B8] text-center mt-6">
            {t('tool_disclaimer_footer')}
          </p>
        </div>
      </section>

      <footer className="bg-[#0F172A] text-white py-8 print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="font-bold text-xl font-['Outfit'] text-[#0596DE]">Tabbibak.dz</span>
          <div className="border-t border-gray-800 mt-6 pt-6 text-gray-500 text-sm">
            © {new Date().getFullYear()} Tabbibak.dz
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ToolPageLayout;
